import { useEffect, useMemo, useState, useRef } from "react";
import moment from "moment";
import { getStudentSkillsMastery } from "@/services/weeklyPlanner/query/getStudentSkillsMastery";

interface UseReTeachMasteryDataProps {
  students: any[];
  skillIds: string[];
  mondayDate: string;
}

interface MasteryData {
  [studentId: string]: {
    [skillId: string]: {
      mondayStatus?: string;
      currentStatus?: string;
      exposureCount?: number;
      errorCount?: number;
    };
  };
}

export function useReTeachMasteryData({ 
  students, 
  skillIds, 
  mondayDate
}: UseReTeachMasteryDataProps) {
  const todayDate = moment().startOf('day').format('YYYY-MM-DD');
  // console.log("🚀 ~ todayDate:", todayDate)
  // console.log("🚀 ~ mondayDate:", mondayDate)
  const [masteryData, setMasteryData] = useState<MasteryData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedMastery = useRef(false);

  // Memoize the student IDs and skill IDs to prevent unnecessary re-renders
  // Use JSON.stringify for deep comparison to prevent unnecessary re-renders
  const studentIds = useMemo(() => {
    const ids = students.map(s => s.id).sort();
    return JSON.stringify(ids);
  }, [students]);
  
  const memoizedSkillIds = useMemo(() => {
    const sortedIds = [...skillIds].sort();
    return JSON.stringify(sortedIds);
  }, [skillIds]);

  // Parse the JSON strings back to arrays for API calls
  const studentIdsArray = useMemo(() => JSON.parse(studentIds), [studentIds]);
  const skillIdsArray = useMemo(() => JSON.parse(memoizedSkillIds), [memoizedSkillIds]);

  // Fetch mastery data for Monday and Today
  useEffect(() => {
    if (!students.length || !skillIds.length || !mondayDate || hasFetchedMastery.current) {
      return;
    }

    const fetchMasteryData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch Monday mastery data
        const mondayResponse = await getStudentSkillsMastery({
          studentId: studentIdsArray,
          skillId: skillIdsArray,
          dateFrom: mondayDate,
          dateTo: mondayDate,
        });

        // Fetch Today mastery data
        const todayResponse = await getStudentSkillsMastery({
          studentId: studentIdsArray,
          skillId: skillIdsArray,
          dateFrom: todayDate,
          dateTo: todayDate,
        });

        // Process and combine the data
        const processedData: MasteryData = {};

        // Initialize data structure
        students.forEach(student => {
          processedData[student.id] = {};
          skillIds.forEach(skillId => {
            processedData[student.id][skillId] = {};
          });
        });

        // Process Monday data
        if (mondayResponse?.studentSkillsMastery) {
          mondayResponse.studentSkillsMastery.forEach((studentData: any) => {
            const studentId = studentData.studentId;
            if (processedData[studentId]) {
              studentData.records.forEach((record: any) => {
                if (processedData[studentId][record.skillId]) {
                  processedData[studentId][record.skillId].mondayStatus = record.pacing;
                }
              });
            }
          });
        }

        // Process Today data
        if (todayResponse?.studentSkillsMastery) {
          todayResponse.studentSkillsMastery.forEach((studentData: any) => {
            const studentId = studentData.studentId;
            if (processedData[studentId]) {
              studentData.records.forEach((record: any) => {
                if (processedData[studentId][record.skillId]) {
                  processedData[studentId][record.skillId].currentStatus = record.pacing;
                  processedData[studentId][record.skillId].exposureCount = record.exposureCount;
                  processedData[studentId][record.skillId].errorCount = record.errorCount;
                }
              });
            }
          });
        }

        // DEBUG: Set hardcoded current mastery for all students and skills
        // console.log("🔧 Setting hardcoded current mastery for debugging...");
        // students.forEach(student => {
        //   skillIds.forEach(skillId => {
        //     if (processedData[student.id] && processedData[student.id][skillId]) {
        //       // processedData[student.id][skillId].mondayStatus = MasteryPacing.BELOW_GRADE_LEVEL;
        //       processedData[student.id][skillId].currentStatus = MasteryPacing.BELOW_GRADE_LEVEL;
        //     }
        //   });
        // });

        // console.log("🔧 Final processed mastery data:", processedData);
        setMasteryData(processedData);
        hasFetchedMastery.current = true;
      } catch (err) {
        console.error("Error fetching mastery data:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch mastery data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMasteryData();
  }, [studentIds, memoizedSkillIds, mondayDate, todayDate]);

  // Reset fetch flag when key dependencies change
  useEffect(() => {
    hasFetchedMastery.current = false;
  }, [studentIds, memoizedSkillIds, mondayDate, todayDate]);

  return {
    masteryData,
    isLoading,
    error,
  };
}

export default useReTeachMasteryData; 